import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type {
  Closure,
  ClosureFormValues,
  ListClosuresParams,
} from './schema';
import type { CommonResponse } from '@/types/api';

const ROOT_KEY = 'marketClosures';
const listKey = (params: ListClosuresParams) =>
  [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useClosures(params: ListClosuresParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listClosures(params),
    placeholderData: (prev) => prev,
  });
}

export function useClosure(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getClosure(id as string),
    enabled: !!id,
  });
}

export function useCreateClosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ClosureFormValues) => api.createClosure(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('Closure created');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to create closure')),
  });
}

export function useUpdateClosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: ClosureFormValues }) =>
      api.updateClosure(id, values),
    onSuccess: (updated) => {
      // The update response is the base Closure shape (no translations / apmcs);
      // invalidate the detail key so the next open re-fetches with full data.
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      toast.success('Closure updated');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to update closure')),
  });
}

// Optimistic delete: snapshot every cached list, drop the row, restore on failure.
export function useDeleteClosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteClosure(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: [ROOT_KEY, 'list'] });
      const snapshots = queryClient.getQueriesData<CommonResponse<Closure>>({
        queryKey: [ROOT_KEY, 'list'],
      });
      for (const [key, value] of snapshots) {
        if (!value) continue;
        queryClient.setQueryData<CommonResponse<Closure>>(key, {
          ...value,
          rowData: value.rowData.filter((r) => r.id !== id),
        });
      }
      return { snapshots };
    },
    onError: (err, _id, ctx) => {
      ctx?.snapshots.forEach(([key, value]) =>
        queryClient.setQueryData(key, value),
      );
      toast.error(describeError(err, 'Failed to delete closure'));
    },
    onSettled: (_data, _err, id) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.removeQueries({ queryKey: detailKey(id) });
    },
    onSuccess: () => toast.success('Closure deleted'),
  });
}
